import React from "react";
import Box from "@mui/material/Box";
import { Loader } from "../../atoms/Loader";

// ── Formatters & helpers ──

export function getInitials(name) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function formatCurrency(amount) {
  const num = parseFloat(amount);
  if (isNaN(num)) return "$0.00";
  return "$" + num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Currency-aware: uses Intl for well-known ISO codes, falls back to "CODE 0.00".
export function formatMoney(amount, currency) {
  const num = parseFloat(amount);
  if (isNaN(num)) return formatCurrency(0);
  if (!currency) return formatCurrency(num);
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${currency} ${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

export function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function daysAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  return `${diff}d`;
}

export function daysAgoNum(dateStr) {
  if (!dateStr) return 999;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
}

export function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case "fulfilled":
    case "paid":
    case "active":
      return { bg: "#dcfce7", color: "#166534" };
    case "pending":
    case "unfulfilled":
      return { bg: "#dbeafe", color: "#1e40af" };
    case "refunded":
    case "cancelled":
      return { bg: "#f4f4f5", color: "#3f3f46" };
    default:
      return { bg: "#f4f4f5", color: "#3f3f46" };
  }
}

// ── Shared atoms ──

export function StatusBadge({ status }) {
  const colors = getStatusColor(status);
  return (
    <Box
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: 600,
        px: "8px",
        py: "2px",
        borderRadius: "9999px",
        bgcolor: colors.bg,
        color: colors.color,
        textTransform: "capitalize",
      }}
    >
      {status || "Unknown"}
    </Box>
  );
}

export function SectionTitle({ children, sx = {} }) {
  return (
    <Box
      sx={{
        fontSize: 12,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "#71717a",
        mb: "8px",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function KpiBox({ label, value, color }) {
  return (
    <Box
      sx={{
        textAlign: "center",
        p: "8px 4px",
        bgcolor: "#f4f4f5",
        borderRadius: "6px",
        border: "1px solid #f4f4f5",
      }}
    >
      <Box sx={{ fontSize: 17, fontWeight: 700, color: color || "#09090b" }}>{value}</Box>
      <Box sx={{ fontSize: 11, color: "#71717a", mt: "2px" }}>{label}</Box>
    </Box>
  );
}

export function OrderItem({ order }) {
  const orderName = order?.name || `#${order?.order_number || order?.id || ""}`;
  const status = order?.fulfillment_status || order?.financial_status || "pending";
  const total = formatCurrency(order?.total_price);
  const date = formatDate(order?.created_at);
  const itemCount = order?.line_items_count ?? order?.line_items?.length ?? 0;
  const discount = order?.discount_code || order?.discount_codes?.[0]?.code;
  const adminUrl = order?.admin_url;

  return (
    <Box sx={{ p: "10px 0", borderBottom: "1px solid #f4f4f5", "&:last-child": { borderBottom: "none" } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "4px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ fontSize: 14, fontWeight: 600, color: "#09090b" }}>{orderName}</Box>
          <StatusBadge status={status} />
        </Box>
        <Box sx={{ fontSize: 14, fontWeight: 600 }}>{total}</Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 2, fontSize: 13, color: "#71717a" }}>
          <span>{date}</span>
          <span>
            {itemCount} item{itemCount !== 1 ? "s" : ""}
            {discount ? ` \u00B7 Discount: ${discount}` : ""}
          </span>
        </Box>
        {adminUrl && (
          <Box
            component="a"
            href={adminUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: 12, color: "#09090b", textDecoration: "underline", cursor: "pointer" }}
          >
            View ↗
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function EmptyState({ icon = "📭", title, message, children, tone = "neutral" }) {
  const palette = {
    neutral: { bg: "#f1f5f9", iconColor: "#0f172a" },
    warning: { bg: "#fef3c7", iconColor: "#92400e" },
    danger: { bg: "#fee2e2", iconColor: "#991b1b" },
    info: { bg: "#dbeafe", iconColor: "#1e3a8a" },
  }[tone] || { bg: "#f1f5f9", iconColor: "#0f172a" };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: "40px",
        px: "24px",
        gap: "10px",
        color: "#64748b",
        minHeight: 260,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          bgcolor: palette.bg,
          color: palette.iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 30,
          mb: "4px",
        }}
      >
        {icon}
      </Box>
      {title && (
        <Box sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a", textAlign: "center" }}>
          {title}
        </Box>
      )}
      {message && (
        <Box sx={{ fontSize: 14, color: "#64748b", textAlign: "center", maxWidth: 460, lineHeight: 1.55 }}>
          {message}
        </Box>
      )}
      {children && (
        <Box sx={{ mt: "14px", width: "100%", maxWidth: 520 }}>
          {children}
        </Box>
      )}
    </Box>
  );
}

// ── Shell: outer chrome (tabs bar + body card) shared by every entity widget ──

export function Shell({ tabs = [], activeTab, onTabChange, storeDomain, loading, bare, children }) {
  const showTopBar = tabs.length > 0 || !!storeDomain;
  return (
    <Box sx={{ width: "100%", height: "100vh", bgcolor: "#fafafa", display: "flex", flexDirection: "column" }}>
      {showTopBar && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            px: "16px",
            minHeight: tabs.length > 0 ? "auto" : 36,
            borderBottom: "1px solid #e4e4e7",
            bgcolor: "#fff",
            overflowX: "auto",
          }}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <Box
                key={tab.key}
                component="button"
                onClick={() => onTabChange?.(tab.key)}
                sx={{
                  px: "14px",
                  py: "11px",
                  fontSize: 14,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? "#0f172a" : "#64748b",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  borderBottom: isActive ? "2px solid #0f172a" : "2px solid transparent",
                  mb: "-1px",
                  whiteSpace: "nowrap",
                  fontFamily: "inherit",
                  transition: "color 0.15s, border-color 0.15s",
                  "&:hover": { color: "#0f172a" },
                }}
              >
                {tab.label}
              </Box>
            );
          })}
          {storeDomain && (
            <Box
              sx={{
                ml: "auto",
                fontSize: 12,
                color: "#64748b",
                bgcolor: "#f1f5f9",
                px: "8px",
                py: "3px",
                borderRadius: "9999px",
                fontFamily: "monospace",
              }}
            >
              {storeDomain}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, p: "10px", overflowY: "auto" }}>
        <Box
          sx={
            bare
              ? { minHeight: "100%" }
              : {
                  bgcolor: "#fff",
                  border: "1px solid #e4e4e7",
                  borderRadius: "10px",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  p: "14px",
                  minHeight: "100%",
                }
          }
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 360,
              }}
            >
              <Loader />
            </Box>
          ) : (
            children
          )}
        </Box>
      </Box>
    </Box>
  );
}
